import type { Signal } from '@preact/signals-react';
import { useComputed, useSignalEffect } from '@preact/signals-react';
import type { MutableRefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OdinDataRetrieval } from '../../common/data-retrieval/data-retrieval-interfaces';
import type { OdinKey } from '../../common/types/odin-key.type';
import { filterData, sortData } from '../continuous-scroller/continuous-scroller-functions';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
} from '../continuous-scroller/continuous-scroller-types';
import type { ContinuousScrollerRef } from '../continuous-scroller/continuous-scroller.component';
import ContinuousScroller from '../continuous-scroller/continuous-scroller.component';
import { DataSearchLocation } from '../enums';
import type { JsonData } from '../generic-types';
import OdinIcon from '../odin-icon/odin-icon.component';
import type { Api } from './api/api';
import type { DataGridCellRenderer } from './data-grid-custom-column.interface';
import type { RowTemplateType } from './data-grid-data-row.component';
import DataGridDataRow from './data-grid-data-row.component';
import { getIdFieldColumn } from './data-grid-functions';
import type { DataGridColumnSettings, DataGridRowIndicator } from './data-grid-settings.interface';

export interface DataGridDataProps {
  columns: Signal<Array<DataGridColumnSettings>>;
  selectedRows: Signal<JsonData>;
  highlightedRows: Signal<JsonData>;
  staticData: Signal<JsonData>;
  pageSize: Signal<number>;
  optionsContainerRef: (element: HTMLElement | null) => void;
  data: JsonData | OdinDataRetrieval;
  customData?: JsonData;
  rowIndicator?: DataGridRowIndicator;
  scrollerElement: Signal<HTMLElement | null>;
  filterSignal: Signal<Array<ContinuousScrollerFilter>>;
  sortSignal: Signal<Array<ContinuousScrollerSort>>;
  sortFunction?: (data: JsonData, sorts: Array<ContinuousScrollerSort>) => JsonData;
  filterLocation?: DataSearchLocation;
  minimumPageCount?: number;
  stopRequestsOnNoData?: boolean;
  rowIsClickable?: boolean;
  api: Api;
  components?: Record<string, DataGridCellRenderer>;
  rowTemplate?: RowTemplateType;
  allowCellScrollBar?: boolean;
  autoSizeColumns?: boolean;
  columnWidthAdjustedRef: MutableRefObject<boolean>;
  isPaginated?: boolean;
}

export default function DataGridData({
  columns,
  selectedRows,
  highlightedRows,
  staticData,
  pageSize,
  optionsContainerRef,
  data,
  customData,
  rowIndicator,
  scrollerElement,
  filterSignal,
  sortSignal,
  sortFunction = sortData,
  filterLocation = DataSearchLocation.Local,
  minimumPageCount,
  stopRequestsOnNoData,
  rowIsClickable = true,
  api,
  components,
  rowTemplate,
  allowCellScrollBar = false,
  autoSizeColumns = false,
  columnWidthAdjustedRef,
  isPaginated = true,
}: DataGridDataProps) {
  const [finalData, setFinalData] = useState<JsonData>();
  const [useDataRetriever, setUseDataRetriever] = useState(false);
  const [columnChangedTrigger, setColumnsChangedTrigger] = useState(false);
  const [idFieldName, setIdFieldName] = useState<string>();
  const [scrollingElement, setScrollingElement] = useState<HTMLElement | null>(null);

  const dataRetriever = useRef<OdinDataRetrieval | undefined>(undefined);
  const previousColumns = useRef<Array<DataGridColumnSettings> | undefined>(undefined);
  const columnWidthMap = useRef<Map<OdinKey, number>>(new Map());

  const { t } = useTranslation();

  useEffect(() => {
    if (Array.isArray(data)) {
      setUseDataRetriever(false);
    } else {
      dataRetriever.current = data;
      setUseDataRetriever(true);
    }
  }, [data]);

  useSignalEffect(() => {
    setScrollingElement(scrollerElement.value);
  });

  useSignalEffect(() => {
    if (Array.isArray(data)) {
      let filteredData: JsonData = staticData.value;
      if (filterSignal.value.length > 0) {
        filteredData = filterData(filteredData, filterSignal.value);
      }

      if (sortSignal.value.length > 0) {
        filteredData = sortFunction(filteredData, sortSignal.value);
      }

      setFinalData(filteredData);
    }
  });

  useSignalEffect(() => {
    if (hasColumnsChanged.value) {
      setColumnsChangedTrigger(!columnChangedTrigger);

      // reset column width map
      columnWidthMap.current.clear();
      columnWidthAdjustedRef.current = false;
    }

    if (idColumn.value && idColumn.value.field !== idFieldName) {
      setIdFieldName(idColumn.value.field);
    }
  });

  const idColumn = useComputed(() => getIdFieldColumn(columns.value));

  /**
   * This is needed since the user can change the order of the columns by dragging and dropping them. We need to
   * determine if the columns have actually changed and not just been reordered.
   * @returns {boolean} True if the columns are actually different.
   */
  const hasColumnsChanged = useComputed(() => {
    if (!previousColumns.current) {
      previousColumns.current = columns.value;
      return true;
    }

    if (previousColumns.current?.length !== columns.value.length) {
      return true;
    }

    // the logic here is that every column must have at least 1 match in the previous columns...this code has one flaw
    // if the columns have changed but happen to have the exact same field names then it will return false.
    const columnsChanged =
      columns.value.length > 0 &&
      !columns.value.every((column) =>
        previousColumns.current?.some(
          (previousColumn) => column.id === previousColumn.id && column.field === previousColumn.field
        )
      );

    previousColumns.current = columns.value;

    return columnsChanged;
  });

  const addScrollerRefToApi = (scrollerRef: ContinuousScrollerRef) => {
    api.data.scrollerRef = scrollerRef;
    optionsContainerRef(scrollerRef.getContainerReference());
  };

  const addStaticContainerRefToApi = (container: HTMLDivElement) => {
    api.data.staticDataContainer = container;
    optionsContainerRef(container);
  };

  const dataLoadIndicator = useMemo(
    () => (
      <div className="odin-flex odin-w-full odin-items-center odin-justify-center absolute bottom-0 left-0 odin-bg-mono-1 pt-2">
        <OdinIcon icon="RefreshCw" spin={true} />
        <span className="odin-pl-2">{t('loading-data')}</span>
      </div>
    ),
    [t]
  );

  const renderCustomData = () => {
    if (customData && customData.length > 0) {
      return (
        <div>
          {customData.map((item, index) => {
            const key =
              idFieldName && item[idFieldName]
                ? `custom-row-${item[idFieldName]}`
                : `custom-row${index}-${customData.length}`;

            return (
              <DataGridDataRow
                key={key}
                columns={columns}
                selectedRows={selectedRows}
                highlightedRows={highlightedRows}
                idField={idColumn}
                rowIndex={index}
                data={item}
                isEven={index % 2 === 0}
                rowIndicator={rowIndicator}
                components={components}
                rowIsClickable={rowIsClickable}
                rowTemplate={rowTemplate}
                allowCellScrollBar={allowCellScrollBar}
                autoSizeColumns={autoSizeColumns}
              />
            );
          })}
        </div>
      );
    }

    return null;
  };

  const renderContinuousScroller = () => {
    return (
      dataRetriever.current && (
        <ContinuousScroller
          key={`scroller_${columnChangedTrigger}`}
          dataRetriever={dataRetriever.current}
          scrollingElement={scrollingElement}
          dataLoadIndicator={dataLoadIndicator}
          filterSignal={filterSignal}
          sortSignal={sortSignal}
          sortFunction={sortFunction}
          searchLocation={filterLocation}
          pageSize={pageSize.value}
          minimumPageCount={minimumPageCount}
          stopPageRequestOnZeroPage={stopRequestsOnNoData}
          canUpdateData={true}
          useDataCache={true}
          idFieldName={idFieldName}
          className="!odin-overflow-visible"
          ref={(ref) => {
            if (ref) addScrollerRefToApi(ref);
          }}
          isPaginated={isPaginated}
        >
          {(scrollerData, currentItemCount) => {
            return scrollerData.map((item, index) => (
              <DataGridDataRow
                key={`row${currentItemCount + index}`}
                columns={columns}
                selectedRows={selectedRows}
                highlightedRows={highlightedRows}
                idField={idColumn}
                rowIndex={index}
                data={item}
                isEven={index % 2 === 0}
                rowIndicator={rowIndicator}
                components={components}
                rowIsClickable={rowIsClickable}
                rowTemplate={rowTemplate}
                allowCellScrollBar={allowCellScrollBar}
                autoSizeColumns={autoSizeColumns}
              />
            ));
          }}
        </ContinuousScroller>
      )
    );
  };

  const renderStaticData = () => {
    if (finalData && finalData.length > 0) {
      return (
        <div
          ref={(ref) => {
            if (ref) addStaticContainerRefToApi(ref);
          }}
        >
          {finalData.map((item, index) => {
            const key =
              idFieldName && item[idFieldName] && !Array.isArray(item[idFieldName])
                ? `row-${item[idFieldName]}`
                : `row${index}-${finalData.length}`;

            return (
              <DataGridDataRow
                key={key}
                columns={columns}
                selectedRows={selectedRows}
                highlightedRows={highlightedRows}
                idField={idColumn}
                rowIndex={index}
                data={item}
                isEven={index % 2 === 0}
                rowIndicator={rowIndicator}
                components={components}
                rowIsClickable={rowIsClickable}
                rowTemplate={rowTemplate}
                allowCellScrollBar={allowCellScrollBar}
                autoSizeColumns={autoSizeColumns}
              />
            );
          })}
        </div>
      );
    } else {
      return (
        <div className="odin-h-full odin-w-full odin-text-center">
          <div>{t('no-data', 'No Data')}</div>
        </div>
      );
    }
  };

  return (
    <>
      {renderCustomData()}
      {useDataRetriever ? renderContinuousScroller() : renderStaticData()}
    </>
  );
}
